/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {cloneElement, useCallback, useEffect, useState} from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import RootManager from '../client/RootManager';
import {FilterHashMapKeyType, resolveFilter} from '../filters/FilterHelpers';
import {getFilterSelection} from '../filters/utils';
import {dlsColor} from '../theme/theme';
import useScreenResize from './useScreenResize';

export interface ChipDisplayProps {
  filterSelected: string[];
  optionData?: FilterHashMapKeyType;
  numberChipsLimit?: number;
  containerRef: any;
  sampleChip: JSX.Element;
  shouldDisplayBoundaryChip: boolean;
  shouldDisplayFindNearbyChip?: boolean;
  shouldDisplayDateChip: boolean;
}

/**
 *  This utility hook contains all logic associated with determining the number of visible and hidden chips
 *  abstracted within, with the FilterSelectedOptions component simply using the hook
 *
 * ```typescript
 *     import {useChipDisplay} from '@ion/dls/src/hooks/useChipDisplay;
 *
 *     const {visibleChips, hiddenChips, hidden} = useChipDisplay({
 *     filterSelected,
 *     optionData,
 *     ContainerRef,
 *     sampleChip,
 *     numberChipLimit});
 *
 * ```
 */
export const useChipDisplay = ({
  filterSelected,
  optionData,
  containerRef,
  sampleChip,
  numberChipsLimit,
  shouldDisplayDateChip,
  shouldDisplayBoundaryChip,
  shouldDisplayFindNearbyChip
}: ChipDisplayProps) => {
  const [visibleChips, setVisibleChips] = useState([]);
  const [hiddenChips, setHiddenChips]   = useState([]);

  const calculateExtraWidth = useCallback(() => (
    (shouldDisplayBoundaryChip ? 120 : 0) +
    (shouldDisplayDateChip ? 120 : 0) +
    (shouldDisplayFindNearbyChip ? 120 : 0)
  ), [
    shouldDisplayBoundaryChip,
    shouldDisplayDateChip,
    shouldDisplayFindNearbyChip
  ]);

  // creation of the chip element within #chipsWrapper container to determine it's width
  const getChipWidth = useCallback((label: string) => {
    const parent = RootManager.getContainer('chipsWrapper');
    const virtualChip = cloneElement(sampleChip, {
      key                  : label,
      label                : label,
      id                   : label,
      size                 : 'small',
      maxWidth             : false,
      borderRadius         : '4px',
      labelColor           : dlsColor('grey2'),
      hoverDefault         : true,
      showCustomDeleteIcon : true,
      noTextTransform      : true
    }, null);
    const htmlChip     = renderToStaticMarkup(virtualChip);
    const d            = document.createElement('div');
    d.style.visibility = 'hideen';
    d.style.display    = 'inline-block';
    d.innerHTML        = htmlChip;
    parent.appendChild(d);
    const {width} = d.getBoundingClientRect();
    parent.removeChild(d);
    return width;
  }, []);

  /**
   *  determine the number of items that can be displayed, updating
   *  the visible and hidden states accordingly
   */
  const verifyItems = useCallback((filterSelected) => {
    if (containerRef.current && numberChipsLimit === 0) {
      const containerWidth = containerRef.current.clientWidth;
      const extraWidth     = calculateExtraWidth();
      let occupiedWidth    = 0;
      const visible        = [];
      const hidden         = [];
      let isWidthOccupied  = false;

      filterSelected?.forEach((item) => {
        const key       = item?.selected ? resolveFilter(item?.selected) : item;
        const selected  = optionData?.[item] ?? key;
        const chipWidth = isWidthOccupied ? null : getChipWidth(selected?.title || selected?.label) + 24; // padding
        const newWidth  = isWidthOccupied ? null : occupiedWidth + chipWidth;
        if (!isWidthOccupied && (newWidth + extraWidth <= containerWidth)) {
          occupiedWidth += chipWidth;
          visible.push(item);
        } else {
          isWidthOccupied = true;
          hidden.push(item);
        }
      });
      setVisibleChips(visible);
      setHiddenChips(hidden);
    }
  }, [optionData, shouldDisplayDateChip, shouldDisplayBoundaryChip, shouldDisplayFindNearbyChip]);

  useScreenResize(verifyItems, filterSelected);

  useEffect(() => {
    verifyItems(filterSelected);
  }, [filterSelected]);

  return {
    hidden : hiddenChips?.length,
    visibleChips,
    hiddenChips
  };
};
